import React, { FC, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dismissNotif } from '../reducers/notificationReducer';
import { isNativeMobileApp } from '../utils/platformDetector';
import type { ToastState } from '../types/common';

type AlertSeverity = 'error' | 'success' | 'info' | 'warning';

const isAlertSeverity = (severity: string): severity is AlertSeverity => {
  return ['error', 'success', 'info', 'warning'].includes(severity);
};

const ToastNotif: FC = () => {
  const dispatch: any = useDispatch();
  const queue = useSelector((state: any) => (state.notification as ToastState).queue);
  const current = queue.length ? queue[0] : null;
  const severity = current && isAlertSeverity(current.severity) ? current.severity : 'success';

  const currentId = current?.id;
  const currentDuration = current?.duration;
  // A repeat restarts the clock, so the collapsed toast stays readable.
  const currentCount = current?.count;

  // One timer, owned here. The Snackbar's own autoHideDuration is deliberately
  // not set: two independent timers on one toast meant whichever fired first
  // won, and the reducer's duration was effectively ignored.
  useEffect(() => {
    if (currentId === undefined || currentDuration === undefined) return undefined;
    const timer = setTimeout(() => dispatch(dismissNotif(currentId)), currentDuration);
    return () => clearTimeout(timer);
  }, [currentId, currentDuration, currentCount, dispatch]);

  const handleClose = () => {
    if (currentId !== undefined) dispatch(dismissNotif(currentId));
  };

  // On mobile, float the toast above the app's fixed bottom nav (~57px) and any
  // system inset (gesture bar / home indicator) instead of hugging the screen edge.
  const mobileOffset = isNativeMobileApp()
    ? { '&.MuiSnackbar-root': { bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' } }
    : undefined;

  return (
    <Snackbar
      // Keying on the toast restarts the enter transition between queued items.
      key={currentId ?? 'empty'}
      open={Boolean(current)}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={mobileOffset}
    >
      <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
        {current?.message}
        {current && current.count > 1 ? ` ×${current.count}` : ''}
      </Alert>
    </Snackbar>
  );
};

export default ToastNotif;
